<template>
  <div class="container">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>财务管理</el-breadcrumb-item>
      <el-breadcrumb-item>门店映射</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 主卡：统计区（紧凑）+ 筛选 + 门店映射列表 -->
    <el-card class="main-card">
      <div slot="header" class="toolbar">
        <span class="card-header">门店供应链映射<span v-if="stats.total" class="header-summary">在营 {{ stats.activeTotal }} · 待办 <span class="warn-text">{{ stats.unmaintained }}</span></span></span>
        <div>
          <el-upload
            style="display: inline-block;"
            :action="importUrl"
            :headers="uploadHeaders"
            :show-file-list="false"
            :on-success="handleImportSuccess"
            :on-error="handleImportError"
            :before-upload="beforeImportUpload"
            accept=".xlsx,.xls"
          >
            <el-button type="success" size="medium" icon="el-icon-upload2">导入</el-button>
          </el-upload>
          <el-button size="medium" icon="el-icon-download" @click="handleExport" style="margin-left: 10px;">导出</el-button>
          <el-button type="warning" plain size="medium" icon="el-icon-document" @click="handleDownloadTemplate" style="margin-left: 10px;">下载模板</el-button>
        </div>
      </div>

      <!-- 指标卡：5 张（总量大数字 + 品牌细分小字，门店口径） -->
      <div class="stat-cards">
        <div v-for="card in statCards" :key="card.label" class="stat-card" :class="card.tone">
          <div class="stat-card-num">{{ card.value }}</div>
          <div class="stat-card-label">{{ card.label }}</div>
          <div v-for="(line, i) in card.brandLines" :key="i" class="stat-card-sub">{{ line }}</div>
        </div>
      </div>
      <!-- 筛选行 -->
      <div class="filter-row" style="margin-bottom: 12px; padding-top: 12px; border-top: 1px solid #EBEEF5; flex-wrap: wrap; row-gap: 8px;">
        <el-input v-model="filterKeyword" placeholder="门店/项目/客户/编码" clearable size="medium" style="width: 190px;" @keyup.enter.native="handleFilterChange" @clear="handleFilterChange"></el-input>
        <el-select v-model="filterStoreStatus" placeholder="门店状态" clearable size="medium" style="width: 115px; margin-left: 10px;" @change="handleFilterChange">
          <el-option v-for="s in storeStatusOptions" :key="s" :label="s" :value="s"></el-option>
        </el-select>
        <el-select v-model="filterOperator" placeholder="运营师" clearable filterable size="medium" style="width: 110px; margin-left: 10px;" @change="handleFilterChange">
          <el-option v-for="o in operatorOptions" :key="o._id" :label="o.name" :value="o._id"></el-option>
        </el-select>
        <el-select v-model="filterCurrentOperator" placeholder="现阶段负责人" clearable filterable size="medium" style="width: 130px; margin-left: 10px;" @change="handleFilterChange">
          <el-option v-for="o in operatorOptions" :key="o._id" :label="o.name" :value="o._id"></el-option>
        </el-select>
        <el-select v-model="filterBrand" placeholder="品牌" clearable size="medium" style="width: 105px; margin-left: 10px;" @change="handleFilterChange">
          <el-option v-for="b in stats.brands" :key="b.brand" :label="b.brand" :value="b.brand"></el-option>
        </el-select>
        <el-select v-model="filterScmStatus" placeholder="维护状态" clearable size="medium" style="width: 120px; margin-left: 10px;" @change="handleFilterChange">
          <el-option label="未维护" value="none"></el-option>
          <el-option label="已绑美团" value="meituan"></el-option>
          <el-option label="已绑食亨" value="shiheng"></el-option>
          <el-option label="双侧齐全" value="both"></el-option>
        </el-select>
        <el-button type="primary" size="medium" style="margin-left: 10px;" @click="handleFilterChange">查询</el-button>
        <span class="toolbar-tip" style="margin-left: auto;">共 {{ total }} 条</span>
      </div>

      <el-table :data="list" border stripe v-loading="listLoading" max-height="560" :row-class-name="rowClassName">
        <el-table-column label="#" type="index" width="50" :index="indexMethod"></el-table-column>
        <el-table-column prop="brand" label="品牌" width="90"></el-table-column>
        <el-table-column label="门店 / 项目" min-width="230">
          <template slot-scope="scope">
            <div class="project-name">{{ scope.row.projectName }}<el-tag v-if="scope.row.generation > 1" size="mini" type="warning" style="margin-left: 4px;">第{{ scope.row.generation }}代</el-tag></div>
            <div class="text-content">{{ scope.row.storeName }}/{{ scope.row.customerName }}</div>
            <div v-for="h in scope.row.historyCodes" :key="h.code + h.field" class="text-content">曾用{{ h.field === 'meituan' ? '美团' : '食亨' }}码 {{ h.code }}（已转出）</div>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.status === '翻新中'" type="danger" effect="dark" size="small">翻新中</el-tag>
            <el-tag v-else :type="statusTagType(scope.row.status)" size="small">{{ scope.row.status }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="现阶段负责人" width="100">
          <template slot-scope="scope">
            <span class="text-content">{{ assignmentName(scope.row, 'currentOperator') }}</span>
          </template>
        </el-table-column>
        <el-table-column label="一阶运营" width="90">
          <template slot-scope="scope">
            <span class="text-content">{{ assignmentName(scope.row, 'level1Operator') }}</span>
          </template>
        </el-table-column>
        <el-table-column label="二阶运营" width="90">
          <template slot-scope="scope">
            <span class="text-content">{{ assignmentName(scope.row, 'level2Operator') }}</span>
          </template>
        </el-table-column>
        <el-table-column label="营运督导" width="90">
          <template slot-scope="scope">
            <span class="text-content">{{ assignmentName(scope.row, 'operatingSupervisor') }}</span>
          </template>
        </el-table-column>
        <el-table-column label="美团管家编码" width="130">
          <template slot-scope="scope">
            <span v-if="scope.row.meituanCode">{{ scope.row.meituanCode }}</span>
            <span v-else-if="isTransferred(scope.row)" class="muted-text">已转出</span>
            <span v-else-if="isClosed(scope.row)" class="muted-text">—</span>
            <span v-else class="warn-text">待维护</span>
          </template>
        </el-table-column>
        <el-table-column label="食亨编码" width="110">
          <template slot-scope="scope">
            <span v-if="scope.row.shihengCode">{{ scope.row.shihengCode }}</span>
            <span v-else-if="isTransferred(scope.row)" class="muted-text">已转出</span>
            <span v-else-if="isClosed(scope.row)" class="muted-text">—</span>
            <span v-else class="warn-text">待维护</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="110" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.remark" class="text-content">{{ scope.row.remark }}</span>
            <span v-else class="muted-text">—</span>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" width="150">
          <template slot-scope="scope">
            <span class="text-content">{{ scope.row.updatedAt ? $getDateTime(scope.row.updatedAt) : '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="updatedBy" label="更新人" width="90">
          <template slot-scope="scope">
            <span class="text-content">{{ scope.row.updatedBy || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="80" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="primary" size="mini" @click="openEditDialog(scope.row)">修改</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        style="margin-top: 15px; text-align: right;"
        background
        layout="total, prev, pager, next, jumper"
        :total="total"
        :current-page.sync="pagenum"
        :page-size="pagesize"
        @current-change="fetchList"
      ></el-pagination>
    </el-card>

    <!-- 修改弹窗（无映射=添加，有映射=修改，两码清空保存=删除映射） -->
    <el-dialog :close-on-click-modal="false" :title="editRow && editRow.meituanCode === '' && editRow.shihengCode === '' ? '添加映射' : '修改映射'" :visible.sync="dialogVisible" width="560px">
      <el-form :model="formData" label-width="110px" size="medium">
        <el-form-item label="门店">
          <div>
            <div class="project-name">{{ formData.storeName || formData.projectName }}</div>
            <div class="text-content">项目：{{ formData.projectName }}<span class="sep"></span>客户：{{ formData.customerName }}<span class="sep"></span>状态：{{ formData.status }}</div>
          </div>
        </el-form-item>
        <el-form-item label="美团管家编码">
          <el-input v-model="formData.meituanCode" placeholder="配送单订货机构编号"></el-input>
        </el-form-item>
        <el-form-item label="食亨编码">
          <el-input v-model="formData.shihengCode" placeholder="销售单客户编码"></el-input>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="formData.remark" placeholder="选填" maxlength="200" show-word-limit></el-input>
        </el-form-item>
        <div class="dialog-tip">同一编码只能属于一个项目；两个编码都清空保存将删除该项目的映射</div>
      </el-form>
      <div slot="footer">
        <el-button size="medium" @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" size="medium" :loading="saving" @click="handleSave()">{{ saving ? '保存中…' : '保 存' }}</el-button>
      </div>
    </el-dialog>

    <!-- 导入结果弹窗 -->
    <el-dialog title="导入结果" :visible.sync="importResultVisible" width="720px">
      <div v-if="importResult">
        <el-alert
          :type="importResult.fail > 0 ? 'warning' : 'success'"
          :closable="false"
          :title="`共 ${importResult.total} 条：成功 ${importResult.success} 条，失败 ${importResult.fail} 条`"
          style="margin-bottom: 12px;"
        ></el-alert>
        <el-table v-if="importResult.details && importResult.details.length > 0" :data="importResult.details" border stripe max-height="360" size="small">
          <el-table-column prop="row" label="行号" width="70" align="center"></el-table-column>
          <el-table-column prop="projectName" label="项目名称" min-width="220" show-overflow-tooltip></el-table-column>
          <el-table-column prop="reason" label="失败原因" min-width="260" show-overflow-tooltip></el-table-column>
        </el-table>
      </div>
      <div slot="footer">
        <el-button type="primary" size="medium" @click="importResultVisible = false">知道了</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'FinanceStoreMapping',
  data () {
    return {
      // 统计（门店口径：卡片数字随筛选联动）
      stats: { total: 0, activeTotal: 0, boundMeituan: 0, boundShiheng: 0, unmaintained: 0, closed: 0, brands: [] },
      // 列表
      list: [],
      listLoading: false,
      total: 0,
      pagenum: 1,
      pagesize: 20,
      filterKeyword: '',
      filterStoreStatus: '',
      filterOperator: '',
      filterCurrentOperator: '',
      filterBrand: '',
      filterScmStatus: '',
      // 门店状态选项（与店铺管理页一致；已翻新/已关闭仅存在于历史代，不出现；
      // '关店'=解约/闭店后已在门店地图释放商圈名额的生命周期终态；'服务到期'=代运营自然到期终态）
      storeStatusOptions: ['筹建中', '待运营', '运营中', '翻新中', '停业中', '春节停业', '闭店', '解约', '关店', '服务到期'],

      // 运营部员工列表（下拉数据源，与店铺管理页同接口同口径）
      employeeList: [],

      // 修改弹窗
      dialogVisible: false,
      editRow: null,
      saving: false,
      formData: { projectId: '', projectName: '', storeName: '', customerName: '', status: '', meituanCode: '', shihengCode: '', remark: '' },

      // 导入
      importUrl: this.$http.defaults.baseURL + 'finance/projectScmMap/import',
      uploadHeaders: {
        authorization: window.sessionStorage.getItem('token'),
        Accept: 'application/json'
      },
      importResultVisible: false,
      importResult: null
    }
  },
  created () {
    this.fetchStats()
    this.fetchList()
    this.getEmployees()
  },
  computed: {
    /**
     * 运营师/负责人下拉选项（与店铺管理页 Dianpu.vue 完全同口径：
     * 在职运营部员工按岗位过滤，含名下暂无门店者）
     */
    operatorOptions () {
      return this.employeeList.filter(e => /运营师|运营主管|建店专员/.test(e.job))
    },

    /**
     * 指标卡数据：5 张卡（门店口径 + 品牌细分两行小字，每行 2 品牌；
     * 联动态下 0 值品牌不显示，保持小字干净）
     */
    statCards () {
      const brands = this.stats.brands || []
      const lines = key => {
        const parts = brands.filter(b => b[key] > 0).map(b => `${b.brand} ${b[key]}`)
        return [parts.slice(0, 2).join(' · '), parts.slice(2).join(' · ')].filter(Boolean)
      }
      return [
        { label: '门店总数（在营）', value: this.stats.activeTotal, brandLines: lines('total') },
        { label: '待维护', value: this.stats.unmaintained, tone: 'warn', brandLines: lines('unmaintained') },
        { label: '已绑美团管家', value: this.stats.boundMeituan, tone: 'success', brandLines: lines('boundMeituan') },
        { label: '已绑食亨', value: this.stats.boundShiheng, tone: 'success', brandLines: lines('boundShiheng') },
        { label: '闭店/解约/关店', value: this.stats.closed, tone: 'muted', brandLines: lines('closed') }
      ]
    }
  },
  methods: {
    /**
     * 分页序号（跨页连续）
     */
    indexMethod (index) {
      return (this.pagenum - 1) * this.pagesize + index + 1
    },

    /**
     * 门店状态标签配色（与店铺管理页同映射）
     */
    statusTagType (status) {
      const map = { 运营中: 'success', 待运营: 'warning', 筹建中: 'info', 闭店: 'danger', 解约: 'danger', 关店: 'danger', 停业中: 'warning', 春节停业: 'warning', 翻新中: 'danger', 服务到期: 'info' }
      return map[status] || ''
    },

    /**
     * 角色负责人姓名（无显示 —）
     */
    assignmentName (row, role) {
      const a = row.assignments && row.assignments[role]
      return (a && a.name) || '—'
    },

    /**
     * 已转出：两码皆空但有转让历史（无事可做，不算待维护）
     */
    isTransferred (row) {
      return !row.meituanCode && !row.shihengCode && row.historyCodes && row.historyCodes.length > 0
    },

    /**
     * 闭店/解约/关店门店（统计口径一致：无待办意义，灰显沉底）
     */
    isClosed (row) {
      return row.status === '闭店' || row.status === '解约' || row.status === '关店'
    },

    /**
     * 闭店/解约/关店行灰显
     */
    rowClassName ({ row }) {
      return this.isClosed(row) ? 'rescinded-row' : ''
    },

    /**
     * 加载统计（与搜索区联动：卡片数字跟随筛选；下拉人员名单全量）
     */
    async fetchStats () {
      const { data: res } = await this.$http.get('finance/projectScmMap/stats', { params: this._buildFilterParams() })
      if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
      this.stats = res.data
    },

    /**
     * 加载运营部员工列表（与店铺管理页同接口，无权限限制）
     */
    async getEmployees () {
      const { data: res } = await this.$http.get('yunying/employees/list')
      if (res.meta.status !== 200) return
      this.employeeList = res.data || []
    },

    /**
     * 组装筛选参数（列表查询与导出共用，保证所见即所得）
     */
    _buildFilterParams () {
      const params = {}
      if (this.filterKeyword) params.keyword = this.filterKeyword
      if (this.filterStoreStatus) params.status = this.filterStoreStatus
      if (this.filterOperator) params.operatorId = this.filterOperator
      if (this.filterCurrentOperator) params.currentOperatorId = this.filterCurrentOperator
      if (this.filterBrand) params.brand = this.filterBrand
      if (this.filterScmStatus) params.scmFilter = this.filterScmStatus
      return params
    },

    /**
     * 加载门店映射列表
     */
    async fetchList () {
      this.listLoading = true
      try {
        const params = {
          pagenum: this.pagenum,
          pagesize: this.pagesize,
          ...this._buildFilterParams()
        }
        const { data: res } = await this.$http.get('finance/projectScmMap/list', { params })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.list = res.data.list || []
        this.total = res.data.total || 0
      } finally {
        this.listLoading = false
      }
    },

    /**
     * 筛选变化：回到第一页，列表与统计卡同步刷新（联动）
     */
    handleFilterChange () {
      this.pagenum = 1
      this.fetchList()
      this.fetchStats()
    },

    /**
     * 打开修改弹窗（无映射行=添加，有映射行=修改）
     */
    openEditDialog (row) {
      this.editRow = row
      this.formData = {
        projectId: row.projectId,
        projectName: row.projectName,
        storeName: row.storeName,
        customerName: row.customerName,
        status: row.status,
        meituanCode: row.meituanCode || '',
        shihengCode: row.shihengCode || '',
        remark: row.remark || ''
      }
      this.dialogVisible = true
    },

    /**
     * 保存（两码清空=删除映射，需二次确认）
     */
    async handleSave (transfer, transferDate) {
      const meituanCode = this.formData.meituanCode.trim()
      const shihengCode = this.formData.shihengCode.trim()
      const hadMap = !!(this.editRow && this.editRow.mapId) // 行存在即有映射记录（含已转出行：码空但历史在）
      const hasHistory = !!(this.editRow && this.editRow.historyCodes && this.editRow.historyCodes.length > 0)

      if (!meituanCode && !shihengCode) {
        if (!hadMap) {
          return this.$message.warning('两个编码不能同时为空')
        }
        const tip = hasHistory
          ? '两个编码均已清空，保存后仅清除编码（该项目含转让历史记录，历史保留），确定吗？'
          : '两个编码均已清空，保存将删除该项目的映射（页面显示回到待维护），确定吗？'
        const confirm = await this.$confirm(tip, '删除确认', { type: 'warning' }).catch(() => false)
        if (!confirm) return
      }

      this.saving = true
      try {
        const { data: res } = await this.$http.post(`finance/projectScmMap/save/${this.formData.projectId}`, {
          meituanCode, shihengCode, remark: this.formData.remark.trim(), transfer: !!transfer, transferDate: transferDate || ''
        })
        if (res.meta.status !== 200) {
          // 占用且可转移：弹转让确认（原项目保留历史映射，转移日前单据仍归原项目）
          if (res.data && res.data.transferable) {
            this.saving = false
            const holderList = (res.data.holders || []).map(h => `${h.field === 'meituan' ? '美团' : '食亨'}码「${h.code}」← 项目「${h.holderName}」`).join('；')
            const d = new Date()
            const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
            const prompt = await this.$prompt(
              `${holderList}。确认为转让吗？转移后原项目保留历史映射（转移日前的单据仍归原项目）\n转移日期（补录历史转让时改为实际转让日，当天及之后的单据归本项目）：`,
              '映射转移确认',
              {
                type: 'warning',
                confirmButtonText: '确认转移',
                cancelButtonText: '取消',
                inputValue: today,
                inputPattern: /^\d{4}-\d{2}-\d{2}$/,
                inputErrorMessage: '日期格式：YYYY-MM-DD'
              }
            ).catch(() => false)
            if (!prompt) return
            return await this.handleSave(true, prompt.value)
          }
          return this.$message.error(res.meta.msg)
        }
        this.$message.success(res.data && res.data.transferred ? '已转移并保存' : (res.meta.msg || '保存成功'))
        this.dialogVisible = false
        this.fetchList()
        this.fetchStats()
      } finally {
        this.saving = false
      }
    },

    /**
     * 导入前校验（文件类型）
     */
    beforeImportUpload (file) {
      const isExcel = /\.(xlsx|xls)$/i.test(file.name)
      if (!isExcel) {
        this.$message.error('只能上传 xlsx/xls 文件')
        return false
      }
      return true
    },

    /**
     * 导入成功回调（部分成功模式：失败行弹窗展示）
     */
    handleImportSuccess (res) {
      if (res.meta && res.meta.status !== 200) {
        return this.$message.error(res.meta.msg || '导入失败')
      }
      this.importResult = res.data
      this.importResultVisible = true
      this.fetchList()
      this.fetchStats()
    },

    /**
     * 导入失败回调
     */
    handleImportError () {
      this.$message.error('导入失败，请检查网络后重试')
    },

    /**
     * 导出（所见即所得：跟随当前筛选条件，与列表同口径）
     */
    async handleExport () {
      try {
        const { data } = await this.$http.get('finance/projectScmMap/export', { params: this._buildFilterParams(), responseType: 'blob' })
        this._downloadBlob(data, '门店供应链映射.xlsx')
      } catch (err) {
        this.$message.error('导出失败')
      }
    },

    /**
     * 下载导入模板
     */
    async handleDownloadTemplate () {
      try {
        const { data } = await this.$http.get('finance/projectScmMap/template', { responseType: 'blob' })
        this._downloadBlob(data, '门店供应链映射导入模板.xlsx')
      } catch (err) {
        this.$message.error('下载模板失败')
      }
    },

    /**
     * 通用的 Blob 文件下载方法
     */
    _downloadBlob (blob, filename) {
      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.main-card {
  margin-top: 20px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header {
  font-size: 16px;
  font-weight: bold;
}
.header-summary {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
  margin-left: 12px;
}

.filter-row {
  display: flex;
  align-items: center;
}

.toolbar-tip {
  color: #909399;
  font-size: 12px;
}

/* 表格内小字（与 CustomerList 同风格） */
.text-content {
  font-size: 12px;
  color: #909399;
}
.project-name {
  font-size: 13px;
  color: #303133;
}
.warn-text {
  color: #E6A23C;
  font-size: 12px;
}
.muted-text {
  color: #c0c4cc;
}
.red {
  color: #FF4D4F;
}

/* 指标卡：5 张横排，每卡总量大数字 + 品牌细分小字 */
.stat-cards {
  display: flex;
  gap: 12px;
}
.stat-card {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  background: #FAFBFC;
  text-align: center;
}
.stat-card-num {
  font-size: 24px;
  font-weight: bold;
  line-height: 1.2;
  color: #303133;
}
.stat-card.warn .stat-card-num {
  color: #E6A23C;
}
.stat-card.success .stat-card-num {
  color: #67C23A;
}
.stat-card.muted .stat-card-num {
  color: #909399;
}
.stat-card-label {
  font-size: 12px;
  color: #606266;
  margin-top: 2px;
}
.stat-card-sub {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dialog-tip {
  color: #909399;
  font-size: 12px;
  margin-left: 110px;
}

.sep {
  display: inline-block;
  width: 10px;
}

::v-deep .rescinded-row {
  color: #909399;
}
::v-deep .rescinded-row .project-name {
  color: #909399;
}
</style>
